import React, { useState } from 'react';
import { Edit, Trash2, Check, X } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { formatDate, formatPrice, isLightColor } from '../../utils/helpers';
import { BillingMilestones } from './BillingMilestones';

/**
 * Tarjeta de muestra en el Dashboard
 */
const SampleCard = ({
  sample,
  estados,
  isSelected,
  onToggleSelect,
  onEdit,
  onDelete,
  onPaymentUpdate,
  onFieldUpdate,
  isSuperuser,
}) => {
  // Inline editing state
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (field, value) => {
    setEditingField(field);
    setEditValue(value || '');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingField || !onFieldUpdate) return;
    await onFieldUpdate(sample.id, editingField, editValue || null);
    setEditingField(null);
    setEditValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };
  // Get estado by ID
  const getEstadoById = (id) => estados.find((e) => e.id === id);

  // Get status badges
  const getStatusBadges = () => {
    if (!sample.status_id) return [];
    const statusIds = Array.isArray(sample.status_id) ? sample.status_id : [sample.status_id];
    return statusIds.map(id => getEstadoById(id)).filter(Boolean);
  };

  const statusBadges = getStatusBadges();

  return (
    <Card
      className={`hover:shadow-lg transition-all ${
        isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
      }`}
      data-testid={`sample-card-${sample.id}`}
    >
      <CardContent className="pt-6 relative">
        {/* Checkbox for selection */}
        <div className="absolute top-3 right-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(sample.id)}
            className="w-5 h-5 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        {/* Header con Destino y Estado */}
        <div className="flex justify-between items-start mb-4 pr-8">
          <div>
            <p className="text-xs text-gray-500 uppercase mb-1">Destino</p>
            <p className="font-semibold text-blue-600">{sample.destination}</p>
          </div>
          {statusBadges.length > 0 && (
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase mb-1">
                Estado{statusBadges.length > 1 ? 's' : ''}
              </p>
              <div className="flex flex-wrap gap-1 justify-end">
                {statusBadges.map(estado => (
                  <Badge
                    key={estado.id}
                    style={{
                      backgroundColor: estado.color,
                      color: isLightColor(estado.color) ? '#000000' : '#ffffff',
                    }}
                  >
                    {estado.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Campos ANMAR - Layout completo en 5 columnas */}
        {sample.destination === 'ANMAR' ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {/* Fila 1: Cliente, Producto, Ensayo, Subcategoría, Idioma */}
            <FieldDisplay label="Cliente" value={sample.client} isBold />
            <FieldDisplay label="Producto" value={sample.product} isBold />
            <FieldDisplay label="Ensayo" value={sample.ensayo} isBold />
            <FieldDisplay label="Subcategoría" value={sample.subcategory} />
            <FieldDisplay 
              label="Idioma" 
              value={Array.isArray(sample.idioma) ? sample.idioma.join(', ') : sample.idioma} 
            />

            {/* Fila 2: Nº GOYA, Nº Challenge, Nº ANMAR, Resultado Otros Ensayos */}
            <FieldDisplay label="Nº GOYA" value={sample.num_ensayo} />
            <FieldDisplay label="Nº Challenge" value={sample.num_challenge} />
            {/* Nº ANMAR editable */}
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nº ANMAR</span>
              {editingField === 'num_anmar' ? (
                <div className="flex items-center gap-1 mt-1">
                  <Input value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleKeyDown} className="h-7 text-sm" autoFocus data-testid={`inline-edit-num_anmar-${sample.id}`} />
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={saveEdit}><Check className="h-3 w-3 text-green-600" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={cancelEdit}><X className="h-3 w-3 text-red-500" /></Button>
                </div>
              ) : (
                <p className="text-sm mt-1 cursor-pointer hover:bg-blue-50 px-1 py-0.5 rounded border border-transparent hover:border-blue-300 transition-colors" onClick={() => startEdit('num_anmar', sample.num_anmar)} title="Clic para editar">
                  {sample.num_anmar || <span className="text-gray-400 italic">N/A</span>}
                </p>
              )}
            </div>
            <FieldDisplay label="Resultado Otros Ensayos" value={sample.resultado_otros_ensayos} />
            <div /> {/* spacer to fill 5th column */}

            {/* Fila 3: Las 4 Fechas */}
            <FieldDisplay label="F. Entrada GOYA" value={formatDate(sample.fecha_entrada)} />
            <FieldDisplay label="F. Envío GOYA" value={formatDate(sample.fecha_envio)} />
            {/* F. Ejecución ANMAR editable */}
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">F. Ejecución ANMAR</span>
              {editingField === 'fecha_ejecucion' ? (
                <div className="flex items-center gap-1 mt-1">
                  <Input value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleKeyDown} className="h-7 text-sm" autoFocus placeholder="Ej: Semana 12" data-testid={`inline-edit-fecha_ejecucion-${sample.id}`} />
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={saveEdit}><Check className="h-3 w-3 text-green-600" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={cancelEdit}><X className="h-3 w-3 text-red-500" /></Button>
                </div>
              ) : (
                <p className="text-sm mt-1 cursor-pointer hover:bg-blue-50 px-1 py-0.5 rounded border border-transparent hover:border-blue-300 transition-colors" onClick={() => startEdit('fecha_ejecucion', sample.fecha_ejecucion)} title="Clic para editar">
                  {sample.fecha_ejecucion || <span className="text-gray-400 italic">N/A</span>}
                </p>
              )}
            </div>
            {/* F. Informe ANMAR editable */}
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">F. Informe ANMAR</span>
              {editingField === 'fecha_informe' ? (
                <div className="flex items-center gap-1 mt-1">
                  <Input type="date" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleKeyDown} className="h-7 text-sm" autoFocus data-testid={`inline-edit-fecha_informe-${sample.id}`} />
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={saveEdit}><Check className="h-3 w-3 text-green-600" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={cancelEdit}><X className="h-3 w-3 text-red-500" /></Button>
                </div>
              ) : (
                <p className="text-sm mt-1 cursor-pointer hover:bg-blue-50 px-1 py-0.5 rounded border border-transparent hover:border-blue-300 transition-colors" onClick={() => startEdit('fecha_informe', sample.fecha_informe || '')} title="Clic para editar">
                  {formatDate(sample.fecha_informe) || <span className="text-gray-400 italic">N/A</span>}
                </p>
              )}
            </div>
          </div>
        ) : (
          /* Campos comunes para otros destinos */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FieldDisplay label="Cliente" value={sample.client} isBold />
            <FieldDisplay label="Producto" value={sample.product} isBold />
            <FieldDisplay label="Nº GOYA" value={sample.num_ensayo} isBold />
          </div>
        )}

        {/* Campo Ensayo para IVAMI */}
        {sample.destination === 'IVAMI' && sample.ensayo_ivami && (
          <div className="mt-4">
            <FieldDisplay label="Ensayo" value={sample.ensayo_ivami} isBold />
          </div>
        )}

        {/* Campos específicos GAIKER/IVAMI/REGULATORY/ESTABILIDADES */}
        {['GAIKER', 'IVAMI', 'REGULATORY', 'ESTABILIDADES'].includes(sample.destination) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Coste GOYA</p>
              <p className="text-sm font-semibold text-blue-600">
                {(sample.coste_goya || sample.precio_compra) ? 
                  `€${formatPrice(sample.coste_goya || sample.precio_compra)}` : 'N/A'}
              </p>
            </div>
            <FieldDisplay 
              label="Nº Presupuesto GOYA" 
              value={sample.num_presupuesto_goya || sample.presupuesto_goya} 
            />
            {!['ESTABILIDADES', 'REGULATORY'].includes(sample.destination) && (
              <FieldDisplay 
                label={sample.destination === 'GAIKER' ? 'Nº Presupuesto GAIKER' : 'Nº Proforma IVAMI'}
                value={sample.destination === 'GAIKER' 
                  ? (sample.num_presupuesto_gaiker || sample.presupuesto_proforma)
                  : (sample.num_proforma_ivami || sample.presupuesto_proforma)
                }
              />
            )}
          </div>
        )}

        {/* Fechas e Idioma - Para GAIKER, IVAMI, REGULATORY (no ANMAR) */}
        {sample.destination !== 'ANMAR' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
            <FieldDisplay label="F. Entrada GOYA" value={formatDate(sample.fecha_entrada)} />
            {sample.destination !== 'REGULATORY' && (
              <FieldDisplay label="F. Envío GOYA" value={formatDate(sample.fecha_envio)} />
            )}
            <FieldDisplay 
              label="Idioma" 
              value={Array.isArray(sample.idioma) ? sample.idioma.join(', ') : sample.idioma} 
            />
          </div>
        )}

        {/* F. Envío Informe - Para GAIKER/IVAMI */}
        {['GAIKER', 'IVAMI'].includes(sample.destination) && sample.fecha_envio_informe && (
          <div className="mt-4">
            <FieldDisplay label="F. Envío Informe" value={formatDate(sample.fecha_envio_informe)} />
          </div>
        )}

        {/* ESTABILIDADES: Fechas Salida Cámara + F. Informe */}
        {sample.destination === 'ESTABILIDADES' && (
          <div className="mt-4 space-y-3">
            {Array.isArray(sample.fechas_salida_camara) && sample.fechas_salida_camara.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 uppercase mb-2">Fechas Salida Cámara</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {sample.fechas_salida_camara.map((sc, idx) => (
                    <div key={idx} className="bg-blue-50 rounded px-3 py-2 border border-blue-100">
                      <p className="text-xs text-blue-600 font-semibold">Salida {sc.numero}</p>
                      <p className="text-sm font-medium">{sc.fecha || 'Sin fecha'}</p>
                      {sc.metodo && sc.metodo !== 'manual' && (
                        <p className="text-xs text-gray-400">{sc.metodo.replace('_', ' ')}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sample.fecha_informe_estabilidades && (
              <FieldDisplay label="F. Informe" value={formatDate(sample.fecha_informe_estabilidades)} />
            )}
          </div>
        )}

        {/* Hitos de Facturación */}
        {['ANMAR', 'GAIKER', 'IVAMI', 'REGULATORY', 'ESTABILIDADES'].includes(sample.destination) && sample.payment_config && (
          <BillingMilestones
            sample={sample}
            onPaymentUpdate={onPaymentUpdate}
          />
        )}

        {/* Observaciones/Incidencias */}
        <div className="mt-4">
          <p className="text-xs text-gray-500 uppercase mb-1">Observaciones / Incidencias</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {sample.observaciones_incidencias || 
             [sample.observaciones, sample.incidencias].filter(Boolean).join(' | ') || 
             'N/A'}
          </p>
        </div>

        {/* Actions */}
        <div className="mt-4 pt-4 border-t flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(sample)}
            data-testid={`edit-sample-${sample.id}`}
          >
            <Edit size={16} className="mr-1" />
            Editar
          </Button>
          {isSuperuser && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onDelete(sample.id)}
              data-testid={`delete-sample-${sample.id}`}
            >
              <Trash2 size={16} className="mr-1" />
              Eliminar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Helper component for field display
const FieldDisplay = ({ label, value, isBold = false }) => (
  <div>
    <p className="text-xs text-gray-500 uppercase mb-1">{label}</p>
    <p className={`text-sm ${isBold ? 'font-medium' : ''}`}>
      {value || 'N/A'}
    </p>
  </div>
);

export default SampleCard;
